import { ApiClients, API_V_LATEST } from "@/axios-config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleErrorToast, nukeClientState } from "@/lib/utils";
import { STALE_TIME_5_MINUTES } from "@/hooks/query-config";

const USER_KEY = "user";
const USER_PROFILE_KEY = "user-profile";
const LOGIN_RESET_FLAG = "login_reset_attempted";

export const useUserProfile = () =>
    useQuery({
        queryKey: [USER_PROFILE_KEY],
        queryFn: async () => {
            try {
                const res =
                    await ApiClients.User.apiUserProfileGet(API_V_LATEST);
                return res.data;
            } catch (err) {
                void handleErrorToast(err);
                return null;
            }
        },
    });

export function useAuth() {
    const queryClient = useQueryClient();

    const { data: userInfo, isLoading: isLoadingUser } = useQuery({
        queryKey: [USER_KEY],
        queryFn: async () => {
            try {
                const res = await ApiClients.User.apiUserInfoGet();
                if (
                    res.status === 200 &&
                    res.data &&
                    typeof res.data === "object"
                ) {
                    return res.data;
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        staleTime: STALE_TIME_5_MINUTES,
        retry: false,
    });

    const loginMutation = useMutation({
        mutationFn: async ({
            email,
            password,
        }: {
            email: string;
            password: string;
        }) => {
            const res = await ApiClients.Identity.apiIdentityLoginPost(
                true,
                false,
                undefined,
                { email, password },
            );
            if (res.status === 200) {
                const alreadyTriedReset =
                    sessionStorage.getItem(LOGIN_RESET_FLAG) === "1";
                if (!alreadyTriedReset) {
                    const verify = await ApiClients.User.apiUserInfoGet().catch(
                        () => null,
                    );
                    const sessionOk =
                        !!verify &&
                        verify.status === 200 &&
                        !!verify.data &&
                        typeof verify.data === "object";
                    if (!sessionOk) {
                        try {
                            sessionStorage.setItem(LOGIN_RESET_FLAG, "1");
                        } catch {
                            // quota / private mode — proceed anyway
                        }
                        await nukeClientState();
                        window.location.replace("/login?reset=1");
                        return await new Promise<never>(() => {});
                    }
                }
                sessionStorage.removeItem(LOGIN_RESET_FLAG);
            }
            return res;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [USER_KEY] });
        },
    });

    const logoutMutation = useMutation({
        mutationFn: () => ApiClients.User.apiUserLogoutPost(),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [USER_KEY] });
        },
    });

    const registerMutation = useMutation({
        mutationFn: async ({
            email,
            password,
        }: {
            email: string;
            password: string;
        }) => {
            const res = await ApiClients.Identity.apiIdentityRegisterPost(
                undefined,
                { email, password },
            );
            return res;
        },
    });

    const forgotPasswordMutation = useMutation({
        mutationFn: async ({ email }: { email: string }) => {
            const res = await ApiClients.Identity.apiIdentityForgotpasswordPost(
                undefined,
                { email },
            );
            return res;
        },
    });

    const resetPasswordMutation = useMutation({
        mutationFn: async ({
            email,
            code,
            newPassword,
        }: {
            email: string;
            code: string;
            newPassword: string;
        }) => {
            const res = await ApiClients.Identity.apiIdentityResetpasswordPost(
                undefined,
                {
                    email,
                    resetCode: code,
                    newPassword,
                },
            );
            return res;
        },
    });

    return {
        userInfo,
        isLoading:
            isLoadingUser ||
            loginMutation.isPending ||
            registerMutation.isPending,
        isLoggedIn: !!userInfo && typeof userInfo === "object",
        loginMutation,
        registerMutation,
        logoutMutation,
        forgotPasswordMutation,
        resetPasswordMutation,
    };
}
